import React, { useEffect, useState } from 'react';
import { Library, Loader2, Plus, Search } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { C } from '@/design-system/dna';
import { anoLabel, errMsg, ETAPA_LABEL } from './shared';

/** Busca no banco pedagógico (BNCC + itens contextualizados), incluindo anos anteriores. */
export function SkillPicker({ componente, grade, onPick, testid = 'dna-skill-picker' }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [prev, setPrev] = useState(true);
  const [all, setAll] = useState(false);
  const [items, setItems] = useState(null);
  const [custom, setCustom] = useState(false);
  useEffect(() => {
    if (!open) return;
    const params = { q: q || undefined, componente: all ? undefined : componente || undefined, limit: 60 };
    if (grade != null && !all) { if (prev) params.ate_ano = grade > 9 ? 3 : grade; else params.ano = grade > 9 ? grade - 9 : grade; }
    const t = setTimeout(() => api.get('/dna-mapa/habilidades', { params }).then(({ data }) => setItems(data.items)).catch(() => setItems([])), 220);
    return () => clearTimeout(t);
  }, [open, q, prev, all, componente, grade]);
  const addCustom = async () => {
    try {
      const { data } = await api.post('/dna-mapa/habilidades', { etapa: grade > 9 ? 'EM' : grade > 5 ? 'EF2' : 'EF1', ano: grade > 9 ? grade - 9 : grade || 1, area: componente || 'Geral', componente: componente || 'Geral', dominio: 'Contextualizado pelo professor', habilidade: q });
      onPick(data); setOpen(false); toast.success('Item contextualizado adicionado ao banco.');
    } catch (e) { toast.error(errMsg(e, 'Falha ao adicionar.')); }
  };
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild><Button type="button" variant="outline" size="sm" data-testid={testid}><Library className="h-4 w-4" /> Banco BNCC</Button></PopoverTrigger>
      <PopoverContent align="start" className="w-[min(560px,92vw)] p-0">
        <div className="space-y-2 border-b p-3" style={{ borderColor: C.line }}>
          <div className="relative"><Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2" style={{ color: C.muted }} /><Input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Código, habilidade, domínio (ex.: frações, fluência, EF35LP01)" className="pl-9" data-testid={`${testid}-busca`} /></div>
          <div className="flex flex-wrap gap-4 text-[12px] font-semibold">
            <label className="flex items-center gap-2"><Switch checked={prev} onCheckedChange={setPrev} data-testid={`${testid}-anteriores`} /> Incluir anos anteriores</label>
            <label className="flex items-center gap-2"><Switch checked={all} onCheckedChange={setAll} data-testid={`${testid}-todos`} /> Todos os componentes/anos</label>
          </div>
        </div>
        <div className="max-h-[320px] overflow-y-auto p-1.5">
          {!items ? <p className="flex items-center gap-2 p-3 text-sm" style={{ color: C.muted }}><Loader2 className="h-4 w-4 animate-spin" /> Buscando…</p>
            : items.map((s) => (
              <button key={s.id} type="button" onClick={() => { onPick(s); setOpen(false); }} className="w-full rounded-xl px-3 py-2 text-left hover:bg-[#F5EEFA]" data-testid={`${testid}-item-${s.codigo}`}>
                <span className="flex flex-wrap items-center gap-2 text-[11px] font-bold" style={{ color: C.purple }}>{s.codigo}<span className="font-semibold" style={{ color: C.muted }}>{ETAPA_LABEL[s.etapa]} · {anoLabel(s.etapa, s.ano)} · {s.componente} · {s.dominio}</span>{s.kind === 'custom' && <span className="rounded-full bg-amber-50 px-1.5 text-amber-700">contextualizado</span>}</span>
                <span className="block text-[12.5px] leading-snug" style={{ color: C.ink }}>{s.habilidade}</span>
              </button>
            ))}
          {items && !items.length && <p className="p-3 text-[12.5px]" style={{ color: C.muted }}>Nada encontrado.</p>}
        </div>
        {q.trim().length >= 5 && (
          <div className="border-t p-2" style={{ borderColor: C.line }}>
            {custom ? <Button size="sm" className="w-full" onClick={addCustom} data-testid={`${testid}-custom-confirmar`}>Adicionar “{q.slice(0, 60)}” como item contextualizado</Button>
              : <Button size="sm" variant="ghost" className="w-full" onClick={() => setCustom(true)} data-testid={`${testid}-custom`}><Plus className="h-4 w-4" /> Não encontrei — contextualizar</Button>}
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
